import React, { useEffect, useRef, useState } from 'react';
import ExcelJS from 'exceljs';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import MathText from './MathText.jsx';
import LearnerWritingEvidence from './LearnerWritingEvidence.jsx';
import { hasPendingRubricEvidence } from '../utils/writingCriterionReview';

function RubricCriterionList({ result, resultIndex, onChange }) {
    return <>
        {hasPendingRubricEvidence(result) && <p role="alert">Provisional total: one or more rubric criteria need evidence review. No mark has been assigned to those criteria. Enter a teacher-reviewed mark before saving.</p>}
        <ul className="method-step-list">
            {result.methodSteps.map((step, stepIndex) => <li key={`${step.step}-${stepIndex}`}>
                <strong><MathText>{step.step}</MathText>:</strong>{' '}
                {step.status === 'needs_evidence' ? 'Awaiting evidence review; no mark assigned.' : `${step.status} — ${step.marksAwarded}/${step.maxMarks}.`}{' '}
                <MathText>{step.feedback}</MathText>
                {result.writingRubric?.criteria && <label>Teacher mark for {step.step}{' '}
                    <input type="number" min="0" max={step.maxMarks} step="0.5" value={step.marksAwarded ?? ''}
                        aria-label={`Criterion ${result.questionNumber} ${step.step} mark`}
                        onChange={event => onChange?.(resultIndex, 'rubricCriterion', { criterionIndex: stepIndex, score: event.target.value })} /> /{step.maxMarks}
                </label>}
            </li>)}
        </ul>
    </>;
}

function formatMark(value) {
    return Number.isInteger(Number(value)) ? String(value) : Number(value).toFixed(2);
}

function escapeCsvCell(value) {
    const str = String(value ?? '').replace(/\r?\n/g, ' ').trim();
    if (/[",]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function slugifyFilename(value) {
    const cleaned = String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return cleaned || 'script';
}

function toPdfSafeText(value) {
    return String(value ?? '')
        .replace(/[\u2014\u2013]/g, '-')
        .replace(/\u2026/g, '...')
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/\u2713/g, '[ok]')
        .replace(/[\u2717\u2718]/g, '[x]')
        .replace(/\u00D7/g, 'x')
        .replace(/\u00F7/g, '/')
        .replace(/\u2264/g, '<=')
        .replace(/\u2265/g, '>=')
        .replace(/\u2260/g, '!=')
        .replace(/\r?\n/g, ' ')
        .replace(/[^\x20-\x7E\xA0-\xFF]/g, '');
}

function wrapPdfLines(text, font, fontSize, maxWidth) {
    const safe = toPdfSafeText(text).trim();
    if (!safe) return [''];
    const words = safe.split(/\s+/);
    const lines = [];
    let currentLine = '';

    for (const word of words) {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth || !currentLine) {
            currentLine = candidate;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    if (currentLine) {
        lines.push(currentLine);
    }
    return lines;
}

function triggerBlobDownload(blob, filename) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 500);
}

function generateQuickMarkOptions(maxMarks) {
    const max = Number(maxMarks) || 0;
    if (max <= 0) return [0];
    if (max === 1) return [0, 0.5, 1];
    if (max <= 5) {
        const options = [];
        for (let i = 0; i <= max; i += 1) {
            options.push(i);
        }
        return options;
    }
    // For questions with > 5 marks, provide sensible quick choices
    const half = Math.round((max / 2) * 2) / 2;
    const set = new Set([0, 1, half, max - 1, max]);
    return Array.from(set).sort((a, b) => a - b);
}

export function buildStruggleAnalysis(marking, batchItems = [], learnerName = '') {
    const results = Array.isArray(marking?.results) ? marking.results : [];
    const struggledQuestions = results
        .map((result, index) => {
            if (hasPendingRubricEvidence(result)) return null;
            const max = Number(result.maxMarks || 0);
            const awarded = Number(result.marksAwarded || 0);
            const lost = Math.max(0, max - awarded);
            const pct = max > 0 ? (awarded / max) * 100 : 100;
            const missedSteps = Array.isArray(result.methodSteps)
                ? result.methodSteps.filter((step) => Number(step.marksAwarded || 0) < Number(step.maxMarks || 0) || step.status === 'not_awarded' || step.status === 'lost')
                : [];
            return {
                ...result,
                index,
                max,
                awarded,
                lost,
                pct,
                missedSteps,
                severity: awarded <= 0 ? 'critical' : pct < 50 ? 'high' : 'moderate',
                severityLabel: awarded <= 0 ? 'Zero marks (0%)' : pct < 50 ? 'Major gap (< 50%)' : 'Partial loss',
            };
        })
        .filter((item) => item && item.lost > 0)
        .sort((a, b) => (a.pct - b.pct) || (b.lost - a.lost));

    const totalLost = struggledQuestions.reduce((sum, item) => sum + item.lost, 0);

    // Class-wide aggregation when marking a batch of scripts
    const validBatchScripts = Array.isArray(batchItems)
        ? batchItems.filter((item) => Array.isArray(item?.marking?.results) && item.marking.results.length > 0)
        : [];
    const classStruggles = [];
    if (validBatchScripts.length > 1) {
        const questionMap = new Map();
        validBatchScripts.forEach((script) => {
            script.marking.results.forEach((res) => {
                if (hasPendingRubricEvidence(res)) return;
                const qKey = String(res.questionNumber || '').trim();
                if (!qKey) return;
                if (!questionMap.has(qKey)) {
                    questionMap.set(qKey, {
                        questionNumber: qKey,
                        section: res.section || '',
                        totalAwarded: 0,
                        totalMax: 0,
                        learnersStruggled: 0,
                        totalLearners: 0,
                        sampleFeedback: res.feedback || '',
                    });
                }
                const entry = questionMap.get(qKey);
                const awarded = Number(res.marksAwarded || 0);
                const max = Number(res.maxMarks || 0);
                entry.totalAwarded += awarded;
                entry.totalMax += max;
                entry.totalLearners += 1;
                if (awarded < max) {
                    entry.learnersStruggled += 1;
                    if (res.feedback && (!entry.sampleFeedback || awarded === 0)) {
                        entry.sampleFeedback = res.feedback;
                    }
                }
            });
        });
        questionMap.forEach((entry) => {
            if (entry.learnersStruggled > 0 && entry.totalMax > 0) {
                classStruggles.push({
                    ...entry,
                    avgPct: (entry.totalAwarded / entry.totalMax) * 100,
                });
            }
        });
        classStruggles.sort((a, b) => (a.avgPct - b.avgPct) || (b.learnersStruggled - a.learnersStruggled));
    }

    const subjectLabel = learnerName ? learnerName : 'This learner';
    let summaryText = `${subjectLabel} achieved full marks across all questions with no identified struggle areas.`;
    if (struggledQuestions.length > 0) {
        const topList = struggledQuestions
            .slice(0, 3)
            .map((q) => `Q${q.questionNumber} (${formatMark(q.awarded)}/${formatMark(q.max)})`)
            .join(', ');
        summaryText = `${subjectLabel} lost ${formatMark(totalLost)} mark${totalLost === 1 ? '' : 's'} across ${struggledQuestions.length} question${struggledQuestions.length === 1 ? '' : 's'}. Main struggle areas: ${topList}.`;
    }

    const pendingCount = results.filter(hasPendingRubricEvidence).length;
    if (pendingCount > 0) summaryText = `${struggledQuestions.length ? summaryText : 'Diagnostic conclusions are provisional.'} ${pendingCount} task(s) awaiting rubric evidence review are excluded from struggle calculations.`;
    return {
        struggledQuestions,
        totalLost,
        classStruggles,
        batchCount: validBatchScripts.length,
        summaryText,
    };
}

export function buildStruggleCsv(marking, batchItems = [], meta = {}) {
    const learnerName = meta.learnerName || marking?.learnerName || '';
    const subjectName = meta.subjectName || '';
    const assessmentName = meta.assessmentName || '';
    const analysis = buildStruggleAnalysis(marking, batchItems, learnerName);
    const totalAwarded = Number(marking?.totalAwarded || 0);
    const totalMarks = Number(marking?.totalMarks || 0);
    const scorePct = totalMarks > 0 ? ((totalAwarded / totalMarks) * 100).toFixed(1) : '0.0';

    const rows = [
        ['Report', 'Learner Struggle Areas & Diagnostic Summary'],
        ['Learner', learnerName || 'Unassigned'],
    ];
    if ((marking?.results || []).some(hasPendingRubricEvidence)) rows.push(['Status', 'Provisional: rubric evidence review pending']);
    if (subjectName) {
        rows.push(['Subject', subjectName]);
    }
    if (assessmentName) {
        rows.push(['Assessment', assessmentName]);
    }
    if (totalMarks > 0) {
        rows.push(['Total Score', `${formatMark(totalAwarded)} / ${formatMark(totalMarks)} (${scorePct}%)`]);
    }
    rows.push(['Marks Lost', formatMark(analysis.totalLost)]);
    rows.push(['Diagnostic Summary', analysis.summaryText]);
    rows.push([]);

    if (analysis.classStruggles.length > 0) {
        rows.push([`Class-Wide Struggle Hotspots (${analysis.batchCount} scripts marked)`]);
        rows.push([
            'Question',
            'Section',
            'Class Average %',
            'Learners Struggled',
            'Total Learners',
            'Common Issue / Diagnostic Feedback',
        ]);
        analysis.classStruggles.forEach((item) => {
            rows.push([
                item.questionNumber,
                item.section || '-',
                `${item.avgPct.toFixed(1)}%`,
                item.learnersStruggled,
                item.totalLearners,
                item.sampleFeedback || '',
            ]);
        });
        rows.push([]);

        const validBatchScripts = Array.isArray(batchItems)
            ? batchItems.filter((item) => Array.isArray(item?.marking?.results) && item.marking.results.length > 0)
            : [];
        if (validBatchScripts.length > 1) {
            rows.push(['Class Batch Learner Struggle Overview']);
            rows.push([
                'Learner',
                'File Name',
                'Marks Awarded',
                'Max Marks',
                'Percentage %',
                'Marks Lost',
                'Struggled Questions',
                'Summary',
            ]);
            validBatchScripts.forEach((script) => {
                const scriptLearner = script.learnerName || script.marking?.learnerName || script.fileName || 'Learner';
                const scriptAnalysis = buildStruggleAnalysis(script.marking, [], scriptLearner);
                const sAwarded = Number(script.marking?.totalAwarded || 0);
                const sMax = Number(script.marking?.totalMarks || 0);
                const sPct = sMax > 0 ? ((sAwarded / sMax) * 100).toFixed(1) : '0.0';
                const qList = scriptAnalysis.struggledQuestions
                    .map((q) => `Q${q.questionNumber} (${formatMark(q.awarded)}/${formatMark(q.max)})`)
                    .join('; ');
                rows.push([
                    scriptLearner,
                    script.fileName || '',
                    formatMark(sAwarded),
                    formatMark(sMax),
                    `${sPct}%`,
                    formatMark(scriptAnalysis.totalLost),
                    qList || 'None (Full marks)',
                    scriptAnalysis.summaryText,
                ]);
            });
            rows.push([]);
        }
    }

    if (Array.isArray(marking?.results) && marking.results.length > 0) {
        rows.push([learnerName ? `${learnerName} - Questions with Lost Marks` : 'Questions with Lost Marks on Script']);
        rows.push([
            'Question',
            'Section',
            'Marks Awarded',
            'Max Marks',
            'Marks Lost',
            'Score %',
            'Severity',
            'Why Marks Were Lost (Feedback)',
            'Missed Method Steps',
            'Learner Answer',
            'Expected Memo Answer',
        ]);

        if (analysis.struggledQuestions.length === 0) {
            rows.push(['All questions', '-', formatMark(totalAwarded), formatMark(totalMarks), '0', '100%', 'None', 'Full marks achieved across all questions.', '', '', '']);
        } else {
            analysis.struggledQuestions.forEach((item) => {
                const missedStepsText = item.missedSteps
                    .map((step) => `${step.step} (${step.marksAwarded}/${step.maxMarks}): ${step.feedback || ''}`)
                    .join(' | ');
                rows.push([
                    item.questionNumber,
                    item.section || '-',
                    formatMark(item.awarded),
                    formatMark(item.max),
                    formatMark(item.lost),
                    `${item.pct.toFixed(0)}%`,
                    item.severityLabel,
                    item.feedback || '',
                    missedStepsText,
                    item.learnerAnswer || '[blank]',
                    item.correctAnswer || '-',
                ]);
            });
        }
    }

    return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
}

export function exportStruggleAreasCsv(marking, batchItems = [], meta = {}) {
    const csv = buildStruggleCsv(marking, batchItems, meta);
    const slug = slugifyFilename(meta.learnerName || marking?.learnerName || (batchItems?.length > 1 ? 'class-batch' : 'script'));
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    triggerBlobDownload(blob, `struggle-areas-${slug}.csv`);
    return csv;
}

export async function exportStruggleAreasExcel(marking, batchItems = [], meta = {}) {
    const learnerName = meta.learnerName || marking?.learnerName || '';
    const subjectName = meta.subjectName || '';
    const assessmentName = meta.assessmentName || '';
    const analysis = buildStruggleAnalysis(marking, batchItems, learnerName);
    const totalAwarded = Number(marking?.totalAwarded || 0);
    const totalMarks = Number(marking?.totalMarks || 0);
    const scorePct = totalMarks > 0 ? ((totalAwarded / totalMarks) * 100).toFixed(1) : '0.0';

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'MarkSure';
    workbook.created = new Date();

    const cellBorder = {
        top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        right: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    };
    const solidFill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

    const sheet = workbook.addWorksheet('Struggle Areas', {
        views: [{ state: 'frozen', ySplit: 9 }],
    });

    sheet.columns = [
        { key: 'q', width: 12 },
        { key: 'sec', width: 10 },
        { key: 'awarded', width: 13 },
        { key: 'max', width: 12 },
        { key: 'lost', width: 12 },
        { key: 'pct', width: 12 },
        { key: 'severity', width: 20 },
        { key: 'feedback', width: 44 },
        { key: 'steps', width: 36 },
        { key: 'learner', width: 34 },
        { key: 'memo', width: 34 },
    ];

    // Row 1: Title Banner
    sheet.mergeCells('A1:K1');
    const titleCell = sheet.getCell('A1');
    titleCell.value = 'MARKSURE — LEARNER STRUGGLE AREAS & DIAGNOSTIC REPORT';
    titleCell.font = { name: 'Calibri', size: 13, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.fill = solidFill('FF0F172A');
    titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    sheet.getRow(1).height = 30;

    // Rows 3-6: Structured Metadata Grid
    const metaRows = [
        ['Learner', learnerName || 'Unassigned', 'Total Score', totalMarks > 0 ? `${formatMark(totalAwarded)} / ${formatMark(totalMarks)} (${scorePct}%)` : '-'],
        ['Subject', subjectName || '-', 'Marks Lost', `${formatMark(analysis.totalLost)} marks`],
        ['Assessment', assessmentName || '-', 'Struggle Questions', `${analysis.struggledQuestions.length} question${analysis.struggledQuestions.length === 1 ? '' : 's'}`],
    ];
    metaRows.forEach((rowValues, idx) => {
        const rNum = 3 + idx;
        const row = sheet.getRow(rNum);
        row.height = 20;
        sheet.getCell(`A${rNum}`).value = rowValues[0];
        sheet.mergeCells(`B${rNum}:D${rNum}`);
        sheet.getCell(`B${rNum}`).value = rowValues[1];
        sheet.getCell(`E${rNum}`).value = rowValues[2];
        sheet.mergeCells(`F${rNum}:K${rNum}`);
        sheet.getCell(`F${rNum}`).value = rowValues[3];

        ['A', 'E'].forEach((col) => {
            const c = sheet.getCell(`${col}${rNum}`);
            c.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF334155' } };
            c.fill = solidFill('FFF1F5F9');
            c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
            c.border = cellBorder;
        });
        ['B', 'C', 'D', 'F', 'G', 'H', 'I', 'J', 'K'].forEach((col) => {
            const c = sheet.getCell(`${col}${rNum}`);
            c.font = { name: 'Calibri', size: 10, bold: col === 'F' && idx === 0, color: { argb: 'FF0F172A' } };
            c.fill = solidFill('FFFFFFFF');
            c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
            c.border = cellBorder;
        });
    });

    // Row 6: Diagnostic Summary
    const summaryRowNum = 6;
    sheet.getRow(summaryRowNum).height = 26;
    sheet.getCell(`A${summaryRowNum}`).value = 'Summary';
    sheet.getCell(`A${summaryRowNum}`).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF334155' } };
    sheet.getCell(`A${summaryRowNum}`).fill = solidFill('FFF1F5F9');
    sheet.getCell(`A${summaryRowNum}`).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    sheet.getCell(`A${summaryRowNum}`).border = cellBorder;

    sheet.mergeCells(`B${summaryRowNum}:K${summaryRowNum}`);
    for (const col of ['B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K']) {
        const c = sheet.getCell(`${col}${summaryRowNum}`);
        c.fill = solidFill('FFF8FAFC');
        c.border = cellBorder;
    }
    const summaryValCell = sheet.getCell(`B${summaryRowNum}`);
    summaryValCell.value = analysis.summaryText;
    summaryValCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF0F172A' } };
    summaryValCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true };

    // Row 8: Section Header
    sheet.mergeCells('A8:K8');
    const secHeader = sheet.getCell('A8');
    secHeader.value = learnerName ? `${learnerName.toUpperCase()} — QUESTIONS WITH LOST MARKS` : 'QUESTIONS WITH LOST MARKS ON THIS SCRIPT';
    secHeader.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FF0F172A' } };
    secHeader.fill = solidFill('FFE2E8F0');
    secHeader.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    sheet.getRow(8).height = 22;

    // Row 9: Table Column Headers
    const headers = [
        'Question',
        'Section',
        'Awarded',
        'Max Marks',
        'Marks Lost',
        'Score %',
        'Severity',
        'Why Marks Were Lost (AI Feedback)',
        'Missed Method Steps',
        'Learner Answer (Script)',
        'Official Memo Answer',
    ];
    const headerRow = sheet.getRow(9);
    headerRow.values = headers;
    headerRow.height = 24;
    headerRow.eachCell((cell, colNumber) => {
        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = solidFill('FF1E293B');
        cell.alignment = {
            vertical: 'middle',
            horizontal: colNumber <= 6 ? 'center' : 'left',
            wrapText: true,
        };
        cell.border = cellBorder;
    });

    if (analysis.struggledQuestions.length === 0) {
        const r = sheet.addRow([
            'All',
            '-',
            formatMark(totalAwarded),
            formatMark(totalMarks),
            '0',
            '100%',
            'Full Marks',
            'No marks were lost on this script — the learner achieved full marks on every question.',
            '-',
            '-',
            '-',
        ]);
        r.height = 24;
        r.eachCell((cell, colNumber) => {
            cell.font = { name: 'Calibri', size: 10, color: { argb: 'FF166534' } };
            cell.fill = solidFill('FFF0FDF4');
            cell.alignment = { vertical: 'middle', horizontal: colNumber <= 6 ? 'center' : 'left', wrapText: true };
            cell.border = cellBorder;
        });
    } else {
        analysis.struggledQuestions.forEach((item, idx) => {
            const missedStepsText = item.missedSteps
                .map((step) => `• ${step.step} (${step.marksAwarded}/${step.maxMarks}): ${step.feedback || ''}`)
                .join('\n');
            const row = sheet.addRow([
                `Q${item.questionNumber}`,
                item.section || '-',
                Number(formatMark(item.awarded)),
                Number(formatMark(item.max)),
                Number(formatMark(item.lost)),
                `${item.pct.toFixed(0)}%`,
                item.severityLabel,
                item.feedback || '-',
                missedStepsText || '-',
                item.learnerAnswer || '[blank]',
                item.correctAnswer || '-',
            ]);

            const maxChars = Math.max(
                String(item.feedback || '').length,
                missedStepsText.length,
                String(item.learnerAnswer || '').length,
                String(item.correctAnswer || '').length
            );
            const estimatedLines = Math.max(1, Math.ceil(maxChars / 38), item.missedSteps.length);
            row.height = Math.min(130, Math.max(24, estimatedLines * 15 + 8));

            const rowBg = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF8FAFC';
            row.eachCell((cell, colNumber) => {
                cell.font = {
                    name: 'Calibri',
                    size: 10,
                    bold: colNumber === 1 || colNumber === 5 || colNumber === 7,
                    color: { argb: colNumber === 5 ? 'FF991B1B' : 'FF0F172A' },
                };
                cell.fill = solidFill(rowBg);
                cell.alignment = {
                    vertical: 'top',
                    horizontal: colNumber <= 6 ? 'center' : 'left',
                    wrapText: true,
                };
                cell.border = cellBorder;

                if (colNumber === 7) {
                    if (item.severity === 'critical') {
                        cell.fill = solidFill('FFFEF2F2');
                        cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FF991B1B' } };
                    } else if (item.severity === 'high') {
                        cell.fill = solidFill('FFFFFBEB');
                        cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FF92400E' } };
                    } else {
                        cell.fill = solidFill('FFF1F5F9');
                        cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: 'FF334155' } };
                    }
                }
            });
        });
    }

    // Second Sheet for Class Batch Hotspots when marking multiple scripts
    if (analysis.classStruggles.length > 0) {
        const classSheet = workbook.addWorksheet('Class Struggle Hotspots');
        classSheet.columns = [
            { width: 16 },
            { width: 12 },
            { width: 16 },
            { width: 20 },
            { width: 16 },
            { width: 58 },
        ];

        classSheet.mergeCells('A1:F1');
        const cTitle = classSheet.getCell('A1');
        cTitle.value = `CLASS-WIDE STRUGGLE HOTSPOTS (${analysis.batchCount} SCRIPTS MARKED)`;
        cTitle.font = { name: 'Calibri', size: 13, bold: true, color: { argb: 'FFFFFFFF' } };
        cTitle.fill = solidFill('FF0F172A');
        cTitle.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
        classSheet.getRow(1).height = 28;

        const cHeader = classSheet.getRow(3);
        cHeader.values = [
            'Question',
            'Section',
            'Class Average %',
            'Learners Struggled',
            'Total Learners',
            'Common Issue / Diagnostic Feedback',
        ];
        cHeader.height = 24;
        cHeader.eachCell((cell, colNumber) => {
            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = solidFill('FF1E293B');
            cell.alignment = { vertical: 'middle', horizontal: colNumber <= 5 ? 'center' : 'left', wrapText: true };
            cell.border = cellBorder;
        });

        analysis.classStruggles.forEach((item, idx) => {
            const r = classSheet.addRow([
                `Q${item.questionNumber}`,
                item.section || '-',
                `${item.avgPct.toFixed(1)}%`,
                item.learnersStruggled,
                item.totalLearners,
                item.sampleFeedback || '-',
            ]);
            const lines = Math.max(1, Math.ceil(String(item.sampleFeedback || '').length / 52));
            r.height = Math.max(22, lines * 15 + 6);
            const bg = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF8FAFC';
            r.eachCell((cell, colNumber) => {
                cell.font = {
                    name: 'Calibri',
                    size: 10,
                    bold: colNumber === 1 || colNumber === 3,
                    color: { argb: colNumber === 3 && item.avgPct < 50 ? 'FF991B1B' : 'FF0F172A' },
                };
                cell.fill = solidFill(colNumber === 3 && item.avgPct < 50 ? 'FFFEF2F2' : bg);
                cell.alignment = { vertical: 'top', horizontal: colNumber <= 5 ? 'center' : 'left', wrapText: true };
                cell.border = cellBorder;
            });
        });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const slug = slugifyFilename(learnerName || (batchItems?.length > 1 ? 'class-batch' : 'script'));
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    triggerBlobDownload(blob, `struggle-areas-${slug}.xlsx`);
    return buffer;
}

export async function exportStruggleAreasPdf(marking, batchItems = [], meta = {}) {
    const learnerName = meta.learnerName || marking?.learnerName || '';
    const subjectName = meta.subjectName || '';
    const assessmentName = meta.assessmentName || '';
    const analysis = buildStruggleAnalysis(marking, batchItems, learnerName);
    const totalAwarded = Number(marking?.totalAwarded || 0);
    const totalMarks = Number(marking?.totalMarks || 0);
    const scorePct = totalMarks > 0 ? ((totalAwarded / totalMarks) * 100).toFixed(1) : '0.0';

    const pdfDoc = await PDFDocument.create();
    const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const pageWidth = 595.28; // A4 width
    const pageHeight = 841.89; // A4 height
    const margin = 38;
    const bottomSafe = 44;
    const contentWidth = pageWidth - margin * 2;

    const colors = {
        navy: rgb(0.06, 0.09, 0.16),
        slateDark: rgb(0.12, 0.16, 0.23),
        slate: rgb(0.28, 0.33, 0.41),
        muted: rgb(0.42, 0.47, 0.55),
        border: rgb(0.82, 0.86, 0.91),
        borderLight: rgb(0.89, 0.92, 0.95),
        bgSoft: rgb(0.97, 0.98, 0.99),
        bgHeader: rgb(0.94, 0.96, 0.98),
        white: rgb(1, 1, 1),
        critBg: rgb(0.996, 0.95, 0.95),
        critBorder: rgb(0.97, 0.75, 0.75),
        critText: rgb(0.6, 0.11, 0.11),
        highBg: rgb(1, 0.98, 0.92),
        highBorder: rgb(0.97, 0.85, 0.55),
        highText: rgb(0.57, 0.25, 0.05),
        modBg: rgb(0.95, 0.96, 0.98),
        modBorder: rgb(0.82, 0.86, 0.91),
        modText: rgb(0.2, 0.25, 0.33),
        greenBg: rgb(0.94, 0.99, 0.96),
        greenText: rgb(0.09, 0.4, 0.24),
    };

    let page = pdfDoc.addPage([pageWidth, pageHeight]);
    let cursorY = pageHeight - margin;

    const ensureSpace = (neededHeight) => {
        if (cursorY - neededHeight < bottomSafe) {
            page = pdfDoc.addPage([pageWidth, pageHeight]);
            cursorY = pageHeight - margin;
        }
    };

    // 1. Top Dark Banner
    const bannerHeight = 54;
    page.drawRectangle({
        x: margin,
        y: cursorY - bannerHeight,
        width: contentWidth,
        height: bannerHeight,
        color: colors.navy,
    });
    page.drawText('MARKSURE ASSESSMENT DIAGNOSTICS', {
        x: margin + 16,
        y: cursorY - 19,
        size: 8,
        font: fontBold,
        color: rgb(0.65, 0.74, 0.86),
    });
    page.drawText('Learner Struggle Areas & Intervention Report', {
        x: margin + 16,
        y: cursorY - 38,
        size: 14.5,
        font: fontBold,
        color: colors.white,
    });

    if (totalMarks > 0) {
        const badgeText = toPdfSafeText(`${formatMark(totalAwarded)} / ${formatMark(totalMarks)} (${scorePct}%)`);
        const badgeWidth = fontBold.widthOfTextAtSize(badgeText, 11) + 22;
        page.drawRectangle({
            x: margin + contentWidth - badgeWidth - 14,
            y: cursorY - 40,
            width: badgeWidth,
            height: 26,
            color: rgb(0.14, 0.2, 0.3),
            borderColor: rgb(0.28, 0.36, 0.48),
            borderWidth: 1,
        });
        page.drawText(badgeText, {
            x: margin + contentWidth - badgeWidth - 3,
            y: cursorY - 31,
            size: 11,
            font: fontBold,
            color: colors.white,
        });
    }
    cursorY -= bannerHeight + 10;

    // 2. Metadata Grid Box (2 Columns x 3 Rows)
    const metaBoxHeight = 58;
    page.drawRectangle({
        x: margin,
        y: cursorY - metaBoxHeight,
        width: contentWidth,
        height: metaBoxHeight,
        color: colors.bgSoft,
        borderColor: colors.border,
        borderWidth: 0.8,
    });
    const halfWidth = contentWidth / 2;
    page.drawLine({
        start: { x: margin + halfWidth, y: cursorY },
        end: { x: margin + halfWidth, y: cursorY - metaBoxHeight },
        thickness: 0.6,
        color: colors.borderLight,
    });

    const leftMeta = [
        ['Learner:', learnerName || 'Unassigned'],
        ['Subject:', subjectName || '-'],
        ['Assessment:', assessmentName || '-'],
    ];
    const rightMeta = [
        ['Total Score:', totalMarks > 0 ? `${formatMark(totalAwarded)} / ${formatMark(totalMarks)} (${scorePct}%)` : '-'],
        ['Marks Lost:', `${formatMark(analysis.totalLost)} mark${analysis.totalLost === 1 ? '' : 's'}`],
        ['Questions Flagged:', `${analysis.struggledQuestions.length} question${analysis.struggledQuestions.length === 1 ? '' : 's'} with lost marks`],
    ];

    leftMeta.forEach(([label, val], idx) => {
        const rowY = cursorY - 16 - idx * 16;
        page.drawText(toPdfSafeText(label), {
            x: margin + 12,
            y: rowY,
            size: 9,
            font: fontBold,
            color: colors.slate,
        });
        page.drawText(toPdfSafeText(val).slice(0, 42), {
            x: margin + 78,
            y: rowY,
            size: 9,
            font: fontRegular,
            color: colors.navy,
        });
    });

    rightMeta.forEach(([label, val], idx) => {
        const rowY = cursorY - 16 - idx * 16;
        page.drawText(toPdfSafeText(label), {
            x: margin + halfWidth + 12,
            y: rowY,
            size: 9,
            font: fontBold,
            color: colors.slate,
        });
        page.drawText(toPdfSafeText(val).slice(0, 42), {
            x: margin + halfWidth + 102,
            y: rowY,
            size: 9,
            font: idx === 1 && analysis.totalLost > 0 ? fontBold : fontRegular,
            color: idx === 1 && analysis.totalLost > 0 ? colors.critText : colors.navy,
        });
    });
    cursorY -= metaBoxHeight + 10;

    // 3. Diagnostic Summary Callout Box
    const summaryLines = wrapPdfLines(analysis.summaryText, fontRegular, 9.5, contentWidth - 24);
    const summaryBoxHeight = 28 + summaryLines.length * 13;
    ensureSpace(summaryBoxHeight + 10);
    page.drawRectangle({
        x: margin,
        y: cursorY - summaryBoxHeight,
        width: contentWidth,
        height: summaryBoxHeight,
        color: colors.bgHeader,
        borderColor: colors.border,
        borderWidth: 0.8,
    });
    page.drawText('DIAGNOSTIC SUMMARY', {
        x: margin + 12,
        y: cursorY - 15,
        size: 8.5,
        font: fontBold,
        color: colors.slate,
    });
    summaryLines.forEach((line, idx) => {
        page.drawText(line, {
            x: margin + 12,
            y: cursorY - 29 - idx * 13,
            size: 9.5,
            font: fontBold,
            color: colors.navy,
        });
    });
    cursorY -= summaryBoxHeight + 14;

    // 4. Class-Wide Struggle Hotspots Table (if batch mode)
    if (analysis.classStruggles.length > 0) {
        ensureSpace(64);
        page.drawText(toPdfSafeText(`CLASS-WIDE STRUGGLE HOTSPOTS (${analysis.batchCount} SCRIPTS MARKED)`), {
            x: margin,
            y: cursorY - 10,
            size: 10,
            font: fontBold,
            color: colors.navy,
        });
        cursorY -= 16;

        // Table Header
        const colWidths = [58, 48, 68, 92, contentWidth - 266];
        const thHeight = 22;
        page.drawRectangle({
            x: margin,
            y: cursorY - thHeight,
            width: contentWidth,
            height: thHeight,
            color: colors.slateDark,
        });
        const colHeaders = ['Question', 'Section', 'Class Avg', 'Learners Affected', 'Common Issue / Diagnostic Feedback'];
        let colX = margin;
        colHeaders.forEach((hText, idx) => {
            page.drawText(hText, {
                x: colX + 8,
                y: cursorY - 14,
                size: 8.5,
                font: fontBold,
                color: colors.white,
            });
            colX += colWidths[idx];
        });
        cursorY -= thHeight;

        analysis.classStruggles.forEach((item, idx) => {
            const issueLines = wrapPdfLines(item.sampleFeedback || '-', fontRegular, 8.5, colWidths[4] - 14);
            const rowHeight = Math.max(22, issueLines.length * 12 + 10);
            ensureSpace(rowHeight + 6);

            page.drawRectangle({
                x: margin,
                y: cursorY - rowHeight,
                width: contentWidth,
                height: rowHeight,
                color: idx % 2 === 0 ? colors.white : colors.bgSoft,
                borderColor: colors.borderLight,
                borderWidth: 0.6,
            });

            const cells = [
                `Q${item.questionNumber}`,
                item.section || '-',
                `${item.avgPct.toFixed(0)}%`,
                `${item.learnersStruggled} / ${item.totalLearners} learners`,
            ];
            let cX = margin;
            cells.forEach((val, cIdx) => {
                page.drawText(toPdfSafeText(val), {
                    x: cX + 8,
                    y: cursorY - 14,
                    size: 8.5,
                    font: cIdx === 0 || cIdx === 2 ? fontBold : fontRegular,
                    color: cIdx === 2 && item.avgPct < 50 ? colors.critText : colors.navy,
                });
                cX += colWidths[cIdx];
            });
            issueLines.forEach((line, lIdx) => {
                page.drawText(line, {
                    x: cX + 8,
                    y: cursorY - 14 - lIdx * 12,
                    size: 8.5,
                    font: fontRegular,
                    color: colors.slateDark,
                });
            });
            cursorY -= rowHeight;
        });
        cursorY -= 16;
    }

    // 5. Individual Script Questions with Lost Marks (Structured Cards)
    if (Array.isArray(marking?.results) && marking.results.length > 0) {
        ensureSpace(44);
        const sectionTitle = learnerName
            ? `${learnerName.toUpperCase()} — QUESTION-BY-QUESTION STRUGGLE BREAKDOWN`
            : 'QUESTION-BY-QUESTION STRUGGLE BREAKDOWN';
        page.drawText(toPdfSafeText(sectionTitle), {
            x: margin,
            y: cursorY - 10,
            size: 10,
            font: fontBold,
            color: colors.navy,
        });
        cursorY -= 18;

        if (analysis.struggledQuestions.length === 0) {
            ensureSpace(42);
            page.drawRectangle({
                x: margin,
                y: cursorY - 34,
                width: contentWidth,
                height: 34,
                color: colors.greenBg,
                borderColor: colors.border,
                borderWidth: 0.8,
            });
            page.drawText('No marks were lost on this script - the learner achieved full marks on every question.', {
                x: margin + 12,
                y: cursorY - 21,
                size: 9.5,
                font: fontBold,
                color: colors.greenText,
            });
            cursorY -= 44;
        } else {
            analysis.struggledQuestions.forEach((item) => {
                const feedbackLines = item.feedback
                    ? wrapPdfLines(item.feedback, fontRegular, 9, contentWidth - 24)
                    : [];
                const stepBlocks = item.missedSteps.map((step) => wrapPdfLines(
                    `- ${step.step} (${step.marksAwarded}/${step.maxMarks}): ${step.feedback || ''}`,
                    fontRegular,
                    8.5,
                    contentWidth - 32
                ));
                const stepLineCount = stepBlocks.reduce((sum, lines) => sum + lines.length, 0);

                const colBoxWidth = (contentWidth - 24) / 2;
                const learnerLines = wrapPdfLines(item.learnerAnswer || '[blank]', fontRegular, 8.5, colBoxWidth - 16);
                const memoLines = wrapPdfLines(item.correctAnswer || '-', fontRegular, 8.5, colBoxWidth - 16);
                const compareLinesCount = Math.max(learnerLines.length, memoLines.length, 1);
                const compareBoxHeight = 22 + compareLinesCount * 11.5;

                const headerBarHeight = 24;
                const feedbackSectionHeight = feedbackLines.length > 0 ? 16 + feedbackLines.length * 12 : 0;
                const stepsSectionHeight = stepLineCount > 0 ? 14 + stepLineCount * 11.5 : 0;
                const cardHeight = headerBarHeight + 10 + feedbackSectionHeight + stepsSectionHeight + compareBoxHeight + 12;

                ensureSpace(cardHeight + 10);

                const cardTopY = cursorY;
                // Outer Card Border & Background
                page.drawRectangle({
                    x: margin,
                    y: cardTopY - cardHeight,
                    width: contentWidth,
                    height: cardHeight,
                    color: colors.white,
                    borderColor: colors.border,
                    borderWidth: 0.85,
                });

                // Card Top Header Strip
                page.drawRectangle({
                    x: margin,
                    y: cardTopY - headerBarHeight,
                    width: contentWidth,
                    height: headerBarHeight,
                    color: colors.bgHeader,
                    borderColor: colors.border,
                    borderWidth: 0.6,
                });

                const qHeading = `Question ${item.questionNumber}${item.section ? `  (Section ${item.section})` : ''}`;
                page.drawText(toPdfSafeText(qHeading), {
                    x: margin + 12,
                    y: cardTopY - 16,
                    size: 10,
                    font: fontBold,
                    color: colors.navy,
                });

                // Severity Pill on Right of Card Header
                const pillLabel = toPdfSafeText(
                    `${formatMark(item.awarded)} / ${formatMark(item.max)} marks (${item.pct.toFixed(0)}%) - ${item.severityLabel}`
                );
                const pillWidth = fontBold.widthOfTextAtSize(pillLabel, 8.2) + 16;
                const sevColors = item.severity === 'critical'
                    ? { bg: colors.critBg, border: colors.critBorder, text: colors.critText }
                    : item.severity === 'high'
                        ? { bg: colors.highBg, border: colors.highBorder, text: colors.highText }
                        : { bg: colors.modBg, border: colors.modBorder, text: colors.modText };

                page.drawRectangle({
                    x: margin + contentWidth - pillWidth - 10,
                    y: cardTopY - 20,
                    width: pillWidth,
                    height: 16,
                    color: sevColors.bg,
                    borderColor: sevColors.border,
                    borderWidth: 0.7,
                });
                page.drawText(pillLabel, {
                    x: margin + contentWidth - pillWidth - 2,
                    y: cardTopY - 15,
                    size: 8.2,
                    font: fontBold,
                    color: sevColors.text,
                });

                let innerY = cardTopY - headerBarHeight - 12;

                // Why marks were lost
                if (feedbackLines.length > 0) {
                    page.drawText('Why marks were lost:', {
                        x: margin + 12,
                        y: innerY,
                        size: 8.5,
                        font: fontBold,
                        color: colors.slateDark,
                    });
                    innerY -= 12;
                    feedbackLines.forEach((line) => {
                        page.drawText(line, {
                            x: margin + 12,
                            y: innerY,
                            size: 9,
                            font: fontRegular,
                            color: colors.slateDark,
                        });
                        innerY -= 12;
                    });
                    innerY -= 2;
                }

                // Missed method steps
                if (stepBlocks.length > 0) {
                    page.drawText('Missed method steps:', {
                        x: margin + 12,
                        y: innerY,
                        size: 8.5,
                        font: fontBold,
                        color: colors.slate,
                    });
                    innerY -= 11.5;
                    stepBlocks.forEach((lines) => {
                        lines.forEach((line) => {
                            page.drawText(line, {
                                x: margin + 18,
                                y: innerY,
                                size: 8.5,
                                font: fontRegular,
                                color: colors.slate,
                            });
                            innerY -= 11.5;
                        });
                    });
                    innerY -= 2;
                }

                // Side-by-Side Comparison Box (Learner wrote vs Expected memo)
                const compareTopY = innerY + 2;
                page.drawRectangle({
                    x: margin + 12,
                    y: compareTopY - compareBoxHeight,
                    width: contentWidth - 24,
                    height: compareBoxHeight,
                    color: colors.bgSoft,
                    borderColor: colors.borderLight,
                    borderWidth: 0.7,
                });
                page.drawLine({
                    start: { x: margin + 12 + colBoxWidth, y: compareTopY },
                    end: { x: margin + 12 + colBoxWidth, y: compareTopY - compareBoxHeight },
                    thickness: 0.6,
                    color: colors.borderLight,
                });

                page.drawText('LEARNER WROTE (SCRIPT)', {
                    x: margin + 20,
                    y: compareTopY - 12,
                    size: 7.5,
                    font: fontBold,
                    color: colors.muted,
                });
                learnerLines.forEach((line, lIdx) => {
                    page.drawText(line, {
                        x: margin + 20,
                        y: compareTopY - 24 - lIdx * 11.5,
                        size: 8.5,
                        font: fontRegular,
                        color: colors.navy,
                    });
                });

                page.drawText('EXPECTED MEMO SOLUTION', {
                    x: margin + 20 + colBoxWidth,
                    y: compareTopY - 12,
                    size: 7.5,
                    font: fontBold,
                    color: colors.muted,
                });
                memoLines.forEach((line, mIdx) => {
                    page.drawText(line, {
                        x: margin + 20 + colBoxWidth,
                        y: compareTopY - 24 - mIdx * 11.5,
                        size: 8.5,
                        font: fontRegular,
                        color: colors.slateDark,
                    });
                });

                cursorY = cardTopY - cardHeight - 10;
            });
        }
    }

    // 6. Add Footer & Page Numbers to All Pages
    const pages = pdfDoc.getPages();
    const totalPages = pages.length;
    pages.forEach((p, idx) => {
        p.drawLine({
            start: { x: margin, y: 30 },
            end: { x: pageWidth - margin, y: 30 },
            thickness: 0.5,
            color: colors.borderLight,
        });
        p.drawText('MarkSure Automated Marking Diagnostic Report', {
            x: margin,
            y: 18,
            size: 8,
            font: fontRegular,
            color: colors.muted,
        });
        const pageLabel = `Page ${idx + 1} of ${totalPages}`;
        const labelW = fontRegular.widthOfTextAtSize(pageLabel, 8);
        p.drawText(pageLabel, {
            x: pageWidth - margin - labelW,
            y: 18,
            size: 8,
            font: fontRegular,
            color: colors.muted,
        });
    });

    const pdfBytes = await pdfDoc.save();
    const slug = slugifyFilename(learnerName || (batchItems?.length > 1 ? 'class-batch' : 'script'));
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    triggerBlobDownload(blob, `struggle-areas-${slug}.pdf`);
    return pdfBytes;
}

export default function AutomatedMarkingResults({
    marking,
    batchItems = [],
    learnerName = '',
    subjectName = '',
    assessmentName = '',
    onChange,
    activeQuestionIndex = 0,
    onSelectQuestionIndex,
    onJumpToPage,
    onJumpToQuestion,
}) {
    const [viewMode, setViewMode] = useState('focus'); // 'focus' | 'table' | 'struggles'
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const focusedCardRef = useRef(null);
    useEffect(() => {
        if (focusedCardRef.current) focusedCardRef.current.scrollTop = 0;
    }, [activeQuestionIndex, viewMode]);

    if (!marking || !Array.isArray(marking.results) || marking.results.length === 0) {
        return null;
    }

    const results = marking.results;
    const safeIndex = Math.max(0, Math.min(activeQuestionIndex, results.length - 1));
    const current = results[safeIndex] || results[0];

    const percentage = marking.totalMarks > 0
        ? (marking.totalAwarded / marking.totalMarks) * 100
        : 0;
    const reviewedQuestions = results.filter((result) => !result.needsReview).length;
    const reviewQuestions = results.length - reviewedQuestions;
    const autoCheckedQuestions = results.filter((result) => result.autoChecked && !result.needsReview).length;
    // One-mark answers that exactly match the memo and already have the full mark are safe to
    // accept in one click; method marks and anything unusual still need the teacher's eye.
    const sameAnswer = (value) => String(value ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
    const exactMatchIndexes = results.reduce((list, result, index) => {
        const learner = sameAnswer(result.learnerAnswer);
        if (result.needsReview && Number(result.maxMarks) === 1 && Number(result.marksAwarded) === 1
            && learner !== '' && learner === sameAnswer(result.correctAnswer)) list.push(index);
        return list;
    }, []);
    const sections = results.reduce((summary, result) => {
        const key = result.section || 'Unsectioned';
        if (!summary[key]) summary[key] = { awarded: 0, maximum: 0, questions: 0 };
        summary[key].awarded += Number(result.marksAwarded || 0);
        summary[key].maximum += Number(result.maxMarks || 0);
        summary[key].questions += 1;
        return summary;
    }, {});

    const goPrev = () => {
        if (safeIndex > 0) {
            onSelectQuestionIndex?.(safeIndex - 1);
        }
    };

    const goNext = () => {
        if (safeIndex < results.length - 1) {
            onSelectQuestionIndex?.(safeIndex + 1);
        }
    };

    const handleQuickMark = (val) => {
        onChange?.(safeIndex, 'marksAwarded', val);
    };

    const stepMark = (delta) => {
        const currentScore = Number(current.marksAwarded) || 0;
        const max = Number(current.maxMarks) || 0;
        const nextScore = Math.min(max, Math.max(0, currentScore + delta));
        onChange?.(safeIndex, 'marksAwarded', nextScore);
    };

    const quickMarks = generateQuickMarkOptions(current.maxMarks);
    const struggleAnalysis = buildStruggleAnalysis(marking, batchItems, learnerName);
    const exportMeta = {
        learnerName,
        subjectName,
        assessmentName,
    };

    const handleExportStruggleExcel = async () => {
        if (exportingExcel) return;
        setExportingExcel(true);
        try {
            await exportStruggleAreasExcel(marking, batchItems, exportMeta);
        } catch {
            exportStruggleAreasCsv(marking, batchItems, exportMeta);
        } finally {
            setExportingExcel(false);
        }
    };

    const handleExportStrugglePdf = async () => {
        if (exportingPdf) return;
        setExportingPdf(true);
        try {
            await exportStruggleAreasPdf(marking, batchItems, exportMeta);
        } finally {
            setExportingPdf(false);
        }
    };

    return (
        <section className="marking-results" aria-live="polite">
            <div className="marking-results-heading">
                <div>
                    <h2>Marking &amp; review workspace</h2>
                </div>
                <div className="marking-header-right">
                    <div className="marking-view-mode-toggle" role="group" aria-label="Review view mode">
                        <button
                            type="button"
                            className={viewMode === 'focus' ? 'selected' : ''}
                            onClick={() => setViewMode('focus')}
                        >
                            Focused review
                        </button>
                        <button
                            type="button"
                            className={viewMode === 'table' ? 'selected' : ''}
                            onClick={() => setViewMode('table')}
                        >
                            All questions
                        </button>
                        <button
                            type="button"
                            className={viewMode === 'struggles' ? 'selected' : ''}
                            onClick={() => setViewMode('struggles')}
                        >
                            Struggle areas ({struggleAnalysis.struggledQuestions.length})
                        </button>
                    </div>
                    <div className="marking-total">
                        <strong>{formatMark(marking.totalAwarded)} / {formatMark(marking.totalMarks)}</strong>
                        <span>{percentage.toFixed(1)}%</span>
                    </div>
                </div>
            </div>

            <div className="marking-review-navigation" aria-label="Question navigation">
                <label>Jump to question
                    <select aria-label="Jump to question" value={safeIndex} onChange={event => onSelectQuestionIndex?.(Number(event.target.value))}>
                        {Object.keys(sections).map(section => <optgroup key={section} label={section}>
                            {results.map((result, index) => (result.section || 'Unsectioned') === section ?
                                <option key={`${result.questionNumber}-${index}`} value={index}>{result.questionNumber} — {formatMark(result.marksAwarded)}/{formatMark(result.maxMarks)}</option> : null)}
                        </optgroup>)}
                    </select>
                </label>
                <button type="button" data-tour="next-unchecked" disabled={reviewQuestions === 0} onClick={() => {
                    for (let offset = 1; offset <= results.length; offset++) {
                        const index = (safeIndex + offset) % results.length;
                        if (results[index].needsReview) { onSelectQuestionIndex?.(index); break; }
                    }
                }}>Next unchecked</button>
                {exactMatchIndexes.length > 0 && (
                    <button
                        type="button"
                        className="btn-accept-exact-matches"
                        data-tour="accept-exact"
                        title="Marks one-mark answers as checked where the learner wrote exactly the memo answer and received the mark."
                        onClick={() => exactMatchIndexes.forEach(index => onChange?.(index, 'needsReview', false))}
                    >
                        ✓ Accept {exactMatchIndexes.length} exact match{exactMatchIndexes.length === 1 ? '' : 'es'}
                    </button>
                )}
                <span>
                    {reviewedQuestions} of {results.length} checked
                    {autoCheckedQuestions > 0 && ` (${autoCheckedQuestions} automatically against your memo)`}
                </span>
            </div>

            {/* Horizontal Question Pills Bar - shown in Focused review mode */}
            {viewMode === 'focus' && (
                <div className="marking-question-strip-container">
                    <div className="marking-question-strip" role="tablist" aria-label="Select question to review">
                        {results.map((result, idx) => {
                            const isAwardedFull = Number(result.marksAwarded) === Number(result.maxMarks) && Number(result.maxMarks) > 0;
                            const isZero = Number(result.marksAwarded) === 0;
                            const isNeedsReview = Boolean(result.needsReview);
                            const isActive = idx === safeIndex;
                            let statusClass = 'status-partial';
                            if (isNeedsReview) {
                                statusClass = 'status-review';
                            } else if (isAwardedFull) {
                                statusClass = 'status-full';
                            } else if (isZero) {
                                statusClass = 'status-zero';
                            }

                            return (
                                <button
                                    key={`pill-${result.questionNumber}-${idx}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    className={`q-nav-pill ${statusClass} ${isActive ? 'is-active' : ''}`}
                                    onClick={() => onSelectQuestionIndex?.(idx)}
                                    title={`Question ${result.questionNumber}: ${formatMark(result.marksAwarded)}/${formatMark(result.maxMarks)} marks${result.reviewReason ? ` · ${result.reviewReason}` : ''}`}
                                >
                                    <span className="pill-qnum">{result.questionNumber}</span>
                                    <span className="pill-score">{formatMark(result.marksAwarded)}/{formatMark(result.maxMarks)}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Focused Review Mode */}
            {viewMode === 'focus' && current && (
                <div className="focused-question-card" ref={focusedCardRef}>
                    <header className="focused-card-header">
                        <div className="focused-header-info">
                            <span className="focused-q-badge">Question {current.questionNumber}</span>
                            {current.section && <span className="focused-section-badge">Section {current.section}</span>}
                            <span className="focused-step-count">
                                Question {safeIndex + 1} of {results.length}
                            </span>
                            {current.confidence != null && current.confidenceSource !== 'not_provided' && Number(current.confidence) > 0 && (
                                <span className="focused-confidence-tag">
                                    {`${Math.round(Number(current.confidence) * 100)}% model-reported confidence`}
                                </span>
                            )}
                        </div>
                        <div className="focused-header-actions">
                            {current.reviewReason && (current.needsReview || current.autoChecked) && (
                                <span className={`focused-review-reason ${current.needsReview ? 'is-flagged' : 'is-auto'}`}>
                                    {current.needsReview ? `Why check: ${current.reviewReason}` : `Auto-checked: ${current.reviewReason}`}
                                </span>
                            )}
                            <button
                                type="button"
                                className={`btn-review-status-toggle ${current.needsReview ? 'is-flagged' : 'is-checked'}`}
                                onClick={() => onChange?.(safeIndex, 'needsReview', !current.needsReview)}
                            >
                                {current.needsReview ? 'Needs review (click when checked)' : 'Checked (click to flag)'}
                            </button>
                        </div>
                    </header>

                    {/* Side-by-side Answers */}
                    <div className="focused-answers-grid">
                        <div className="focused-answer-pane learner-pane">
                            <div className="pane-header">
                                <span className="pane-tag">Learner&apos;s answer (Script)</span>
                            </div>
                            <div className="pane-content">
                                {current.learnerAnswer ? (
                                    <div className="answer-text">
                                        <LearnerWritingEvidence result={current} />
                                    </div>
                                ) : (
                                    <p className="answer-empty">No answer detected on script</p>
                                )}
                            </div>
                        </div>

                        <div className="focused-answer-pane memo-pane">
                            <div className="pane-header">
                                <span className="pane-tag">Official memo solution</span>
                            </div>
                            <div className="pane-content">
                                {current.correctAnswer ? (
                                    <p className="answer-text">
                                        <MathText>{current.correctAnswer}</MathText>
                                    </p>
                                ) : (
                                    <p className="answer-empty">No answer defined in memo</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mark Allocation Controls */}
                    <div className="focused-marking-controls">
                        <div className="mark-stepper-group">
                            <span className="mark-label">Awarded Mark:</span>
                            <div className="mark-stepper-wrapper">
                                <button
                                    type="button"
                                    className="stepper-btn"
                                    onClick={() => stepMark(-0.5)}
                                    disabled={Number(current.marksAwarded) <= 0}
                                    title="Decrease by 0.5"
                                >
                                    −
                                </button>
                                <input
                                    className="focused-mark-input"
                                    type="number"
                                    min="0"
                                    max={current.maxMarks}
                                    step="0.5"
                                    value={current.marksAwarded}
                                    onChange={(event) => onChange?.(safeIndex, 'marksAwarded', event.target.value)}
                                    aria-label="Awarded mark"
                                />
                                <button
                                    type="button"
                                    className="stepper-btn"
                                    onClick={() => stepMark(0.5)}
                                    disabled={Number(current.marksAwarded) >= Number(current.maxMarks)}
                                    title="Increase by 0.5"
                                >
                                    +
                                </button>
                                <span className="mark-denominator">
                                    / <input
                                        className="marking-denominator-input"
                                        type="number"
                                        min="0.5"
                                        max="100"
                                        step="0.5"
                                        value={current.maxMarks}
                                        onChange={(event) => onChange?.(safeIndex, 'maxMarks', event.target.value)}
                                        title="Edit total possible marks for this question (updates class test total)"
                                        aria-label="Total marks for this question"
                                    /> marks
                                </span>
                            </div>
                        </div>

                        {quickMarks.length > 1 && (
                            <div className="quick-marks-group" aria-label="Quick mark values">
                                <span className="quick-marks-label">Quick set:</span>
                                {quickMarks.map((qm) => {
                                    const isSelected = Number(current.marksAwarded) === qm;
                                    return (
                                        <button
                                            key={`qm-${qm}`}
                                            type="button"
                                            className={`btn-quick-mark ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleQuickMark(qm)}
                                        >
                                            {qm}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* AI Feedback and Evidence */}
                    <div className="focused-feedback-section">
                        {current.feedback && (
                            <div className="feedback-box">
                                <strong>AI Feedback:</strong>
                                <p><MathText>{current.feedback}</MathText></p>
                            </div>
                        )}

                        <div className="evidence-box">
                            <div className="evidence-header">
                                <strong>Paper evidence:</strong>
                                {onJumpToQuestion ? <button type="button" className="btn-jump-paper-page"
                                    onClick={() => onJumpToQuestion(current.questionNumber)}>Show on paper</button> : Number(current.evidencePage) > 0 && (
                                    <button
                                        type="button"
                                        className="btn-jump-paper-page"
                                        onClick={() => onJumpToPage?.(Number(current.evidencePage))}
                                        title={`Jump paper viewer to page ${current.evidencePage}`}
                                    >
                                        Page {current.evidencePage}
                                    </button>
                                )}
                            </div>
                            <p className="evidence-text">
                                {current.evidenceText ? (
                                    <>
                                        &ldquo;<MathText>{current.evidenceText}</MathText>&rdquo;
                                    </>
                                ) : (
                                    'No direct text quote supplied'
                                )}
                            </p>
                        </div>

                        {current.methodSteps?.length > 0 && (
                            <details className="focused-method-steps">
                                <summary>{['essay', 'transactional'].includes(current.kind) ? 'Writing rubric breakdown' : 'Method-marking breakdown'} ({current.methodSteps.length} {['essay', 'transactional'].includes(current.kind) ? 'criteria' : 'steps'})</summary>
                                <RubricCriterionList result={current} resultIndex={safeIndex} onChange={onChange} />
                            </details>
                        )}
                    </div>

                    {/* Diagnostic summary banner when all questions are checked */}
                    {reviewQuestions === 0 && (
                        <div className="marking-struggle-callout" role="region" aria-label="Learner struggle summary">
                            <div className="struggle-callout-text">
                                <strong>What the learner struggled with:</strong>
                                <span>{struggleAnalysis.summaryText}</span>
                            </div>
                            <div className="struggle-callout-actions">
                                {struggleAnalysis.struggledQuestions.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn-view-struggles"
                                        onClick={() => setViewMode('struggles')}
                                    >
                                        View struggle details
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn-export-struggles"
                                    disabled={exportingExcel}
                                    onClick={handleExportStruggleExcel}
                                >
                                    {exportingExcel ? 'Exporting…' : 'Export Excel'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-export-struggles"
                                    disabled={exportingPdf}
                                    onClick={handleExportStrugglePdf}
                                >
                                    {exportingPdf ? 'Exporting…' : 'Export PDF'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Bottom Nav inside Focused Card */}
                    <footer className="focused-card-nav">
                        <button
                            type="button"
                            className="btn-nav-prev"
                            disabled={safeIndex === 0}
                            onClick={goPrev}
                        >
                            ← Previous question
                        </button>
                        <span className="focused-nav-progress">
                            Question {safeIndex + 1} of {results.length}
                        </span>
                        <button type="button" className="btn-review-next" onClick={() => {
                            onChange?.(safeIndex, 'needsReview', false);
                            if (safeIndex < results.length - 1) onSelectQuestionIndex?.(safeIndex + 1);
                        }}>{safeIndex < results.length - 1 ? 'Mark checked & next' : 'Mark checked'}</button>
                        <button
                            type="button"
                            className="btn-nav-next"
                            disabled={safeIndex === results.length - 1}
                            onClick={goNext}
                        >
                            Next question →
                        </button>
                    </footer>
                </div>
            )}

            {/* Full Table View (Spacious, un-cramped) */}
            {viewMode === 'table' && (
                <>
                    <div className="marking-table-stats-bar" aria-label="Marking summary">
                        <span className="table-stat-chip"><strong>{results.length}</strong> questions</span>
                        <span className="table-stat-chip"><strong>{reviewedQuestions}</strong> checked</span>
                        {reviewQuestions > 0 && (
                            <span className="table-stat-chip warning"><strong>{reviewQuestions}</strong> need review</span>
                        )}
                        {Object.entries(sections).map(([section, summary]) => (
                            <span key={section} className="table-stat-chip section-chip">
                                <strong>Sec {section}:</strong> {formatMark(summary.awarded)}/{formatMark(summary.maximum)} ({summary.maximum > 0 ? ((summary.awarded / summary.maximum) * 100).toFixed(0) : '0'}%)
                            </span>
                        ))}
                    </div>
                    <div className="marking-table-wrapper">
                        <table className="marking-table">
                            <thead>
                                <tr>
                                    <th scope="col">Question</th>
                                    <th scope="col">Learner answer</th>
                                    <th scope="col">Memo answer</th>
                                    <th scope="col">Mark &amp; status</th>
                                    <th scope="col">Feedback</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((result, index) => {
                                    const isRowActive = index === safeIndex;
                                    return (
                                        <tr
                                            key={`${result.questionNumber}-${result.maxMarks}-${index}`}
                                            className={`${result.needsReview ? 'needs-review' : ''} ${isRowActive ? 'active-row' : ''}`}
                                            onClick={() => onSelectQuestionIndex?.(index)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <th scope="row" className="table-q-cell">
                                                <div className="table-q-num">
                                                    {result.questionNumber}
                                                    {isRowActive && <span className="active-dot"> ●</span>}
                                                </div>
                                                {result.section && <span className="table-sec-badge">Sec {result.section}</span>}
                                            </th>
                                            <td className="table-answer-cell"><LearnerWritingEvidence result={result} /></td>
                                            <td className="table-answer-cell"><MathText>{result.correctAnswer}</MathText></td>
                                            <td className="mark-cell" onClick={(e) => e.stopPropagation()}>
                                                <div className="table-mark-Editor">
                                                    <input
                                                        className="marking-mark-input"
                                                        type="number"
                                                        min="0"
                                                        max={result.maxMarks}
                                                        step="0.5"
                                                        value={result.marksAwarded}
                                                        onChange={(event) => onChange?.(index, 'marksAwarded', event.target.value)}
                                                    />
                                                    <span className="table-mark-slash">/</span>
                                                    <input
                                                        className="marking-maxmark-input"
                                                        type="number"
                                                        min="0.5"
                                                        max="100"
                                                        step="0.5"
                                                        value={result.maxMarks}
                                                        onChange={(event) => onChange?.(index, 'maxMarks', event.target.value)}
                                                        title="Edit total marks for this question"
                                                        aria-label="Total marks for this question"
                                                    />
                                                </div>
                                                <label className="marking-confidence" title={result.reviewReason || undefined}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!result.needsReview}
                                                        onChange={(event) => onChange?.(index, 'needsReview', !event.target.checked)}
                                                    />
                                                    {result.needsReview ? 'Needs review' : (result.autoChecked ? 'Auto-checked' : 'Checked')}
                                                </label>
                                            </td>
                                            <td className="table-feedback-cell">
                                                <MathText>{result.feedback}</MathText>
                                                {result.marksAwarded > 0 && result.evidenceText && (
                                                    <div className="marking-evidence">
                                                        <strong>{Number(result.evidencePage) > 0 ? `Evidence (P${result.evidencePage}):` : 'Paper excerpt:'}</strong> <MathText>{result.evidenceText}</MathText>
                                                    </div>
                                                )}
                                                {result.methodSteps?.length > 0 && (
                                                    <details onClick={(e) => e.stopPropagation()}>
                                                        <summary>{['essay', 'transactional'].includes(result.kind) ? 'Rubric criteria' : 'Method steps'} ({result.methodSteps.length})</summary>
                                                        <RubricCriterionList result={result} resultIndex={index} onChange={onChange} />
                                                    </details>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Struggle Areas / Diagnostic View */}
            {viewMode === 'struggles' && (
                <div className="marking-struggles-panel" role="region" aria-label="Areas learners struggled with">
                    <div className="struggle-summary-banner">
                        <div className="struggle-banner-header">
                            <div>
                                <h3>Diagnostic Summary: What Learners Struggled With</h3>
                                <p>{struggleAnalysis.summaryText}</p>
                            </div>
                            <div className="struggle-export-actions" aria-label="Export struggle areas report">
                                <button
                                    type="button"
                                    className="btn-export-struggles"
                                    disabled={exportingExcel}
                                    onClick={handleExportStruggleExcel}
                                >
                                    {exportingExcel ? 'Exporting Excel…' : 'Export Excel'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-export-struggles primary"
                                    disabled={exportingPdf}
                                    onClick={handleExportStrugglePdf}
                                >
                                    {exportingPdf ? 'Exporting PDF…' : 'Export PDF'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {struggleAnalysis.classStruggles.length > 0 && (
                        <div className="struggle-batch-section">
                            <h4>Class-Wide Struggle Hotspots ({struggleAnalysis.batchCount} scripts marked)</h4>
                            <div className="struggle-cards-list">
                                {struggleAnalysis.classStruggles.map((item) => (
                                    <div key={`class-struggle-${item.questionNumber}`} className="struggle-item-card class-hotspot">
                                        <div className="struggle-card-top">
                                            <div>
                                                <strong>Question {item.questionNumber}</strong>
                                                {item.section && <span className="focused-section-badge">Section {item.section}</span>}
                                            </div>
                                            <span className="struggle-severity-pill high">
                                                Class avg: {item.avgPct.toFixed(0)}% ({item.learnersStruggled}/{item.totalLearners} learners lost marks)
                                            </span>
                                        </div>
                                        {item.sampleFeedback && (
                                            <p className="struggle-feedback-text">
                                                <strong>Common issue:</strong> <MathText>{item.sampleFeedback}</MathText>
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="struggle-individual-section">
                        <h4>
                            {learnerName ? `${learnerName}'s Questions with Lost Marks` : 'Questions with Lost Marks on This Script'}
                        </h4>
                        {struggleAnalysis.struggledQuestions.length === 0 ? (
                            <p className="struggle-empty-state">
                                No marks were lost on this script — the learner achieved full marks on every question.
                            </p>
                        ) : (
                            <div className="struggle-cards-list">
                                {struggleAnalysis.struggledQuestions.map((item) => (
                                    <div key={`struggle-q-${item.questionNumber}-${item.index}`} className={`struggle-item-card severity-${item.severity}`}>
                                        <div className="struggle-card-top">
                                            <div className="struggle-card-title">
                                                <strong>Question {item.questionNumber}</strong>
                                                {item.section && <span className="focused-section-badge">Section {item.section}</span>}
                                                <span className={`struggle-severity-pill ${item.severity}`}>
                                                    {formatMark(item.awarded)} / {formatMark(item.max)} marks ({item.pct.toFixed(0)}%) — {item.severityLabel}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn-jump-paper-page"
                                                onClick={() => {
                                                    onSelectQuestionIndex?.(item.index);
                                                    setViewMode('focus');
                                                }}
                                            >
                                                Review question
                                            </button>
                                        </div>
                                        {item.feedback && (
                                            <p className="struggle-feedback-text">
                                                <strong>Why marks were lost:</strong> <MathText>{item.feedback}</MathText>
                                            </p>
                                        )}
                                        {item.missedSteps.length > 0 && (
                                            <ul className="method-step-list struggle-missed-steps">
                                                {item.missedSteps.map((step, sIdx) => (
                                                    <li key={`missed-${item.questionNumber}-${sIdx}`}>
                                                        <strong><MathText>{step.step}</MathText> ({step.marksAwarded}/{step.maxMarks}):</strong> <MathText>{step.feedback}</MathText>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        <div className="struggle-answers-compare">
                                            <div><strong>Learner wrote:</strong> <LearnerWritingEvidence result={item} /></div>
                                            <span><strong>Expected memo:</strong> <MathText>{item.correctAnswer || '-'}</MathText></span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
